import { EditorPickItem } from "@/types/dto/category.dto";
import { useProductEngagement } from "@/hooks/useProductEngagement";
import { CheckCircle2, ChevronRight, Star } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { cn } from "@/lib/utils";

type EditorsPickProps = {
	editorsPick: EditorPickItem[];
};

const SingleEditorPickCard = ({
	product,
	review,
	points,
	label,
	isCenter,
}: EditorPickItem & { isCenter?: boolean }) => {
	const { rating } = useProductEngagement(product.slug);

	return (
		<div
			className={`flex flex-col justify-between border shadow-lg transition-all duration-300 hover:cursor-pointer
    md:basis-1/3 relative
    ${isCenter ? "-translate-y-5 scale-y-105" : ""}
    px-7 py-7`}
		>
			{label ? (
				<div className="absolute -ml-[60px] -mt-12">
					<div className="w-max bg-gradient-to-br from-blue-500 to-brand-primary px-4 py-2 text-sm text-white">
						{label}
					</div>

					<div className="h-0 w-0 border-b-[16px] border-r-[32px] border-b-transparent border-r-blue-600"></div>
				</div>
			) : null}

			<div className="mb-6 flex flex-col items-center gap-2">
				<div className={cn("mx-auto py-4", isCenter ? "h-28 w-28" : "h-24 w-24")}>
					<Image
						src={product.logo || ""}
						alt={product.name}
						className="h-full w-full rounded object-contain p-1"
						width={480}
						height={480}
					/>
				</div>

				<div className="flex gap-1">
					{[...Array(5)].map((_, i) => {
						const fullStars = Math.floor(Number(rating));
						const remainder = Number(rating) - fullStars;
						const fillPercent = i < fullStars ? 100 : i === fullStars ? remainder * 100 : 0;

						return (
							<div key={i} className="relative h-5 w-5 sm:h-6 sm:w-6">
								<Star className="h-full w-full fill-gray-300 text-gray-300" />

								{fillPercent > 0 && (
									<div
										className="absolute left-0 top-0 h-full w-full overflow-hidden"
										style={{
											clipPath: `inset(0 ${100 - fillPercent}% 0 0)`,
										}}
									>
										<Star className="h-full w-full fill-yellow-400 text-yellow-400" />
									</div>
								)}
							</div>
						);
					})}
				</div>

				<p className="text-lg font-semibold">{rating}</p>
			</div>

			<div className="flex-1">
				<h3 className="text-md py-2 font-semibold uppercase text-gray-500">Review Verdict</h3>
				<p className="text-sm text-gray-500">{review}</p>

				<ul className="mt-6 flex flex-col items-start gap-2">
					{points.map((point) => (
						<li key={point} className="flex gap-2">
							<div className="h-6 w-6">
								<CheckCircle2 className="h-6 w-6 fill-brand-primary text-white" />
							</div>
							<span className="text-sm font-light text-gray-500">{point}</span>
						</li>
					))}
				</ul>
			</div>

			<div className="pt-8">
				<Link
					href={`/products/${product.slug || "#"}`}
					className="flex items-center justify-center gap-2 border border-transparent bg-red-500 px-6 py-2 text-white transition-all duration-300 hover:bg-red-600 focus:border-red-700 focus:outline-none focus:ring"
				>
					Visit Provider <ChevronRight className="h-5 w-5" />
				</Link>
			</div>
		</div>
	);
};

const EditorsPick = ({ editorsPick }: EditorsPickProps) => {
	return (
		<div className="mx-auto mb-16 max-w-6xl">
			<div className="mb-6 py-12 md:py-12">
				<h1 className="mb-4 text-center text-xl font-bold md:text-3xl">
					Editor{"'"}s picks for {new Date().getFullYear()}
				</h1>
				<div className="mx-auto my-3 h-1 w-20 rounded bg-brand-primary" />
			</div>

			<div className="flex flex-wrap items-stretch gap-4 md:flex-nowrap">
				{editorsPick.map((item, index) => (
					<SingleEditorPickCard key={item.product.id} {...item} isCenter={index === 1} />
				))}
			</div>
		</div>
	);
};

export default EditorsPick;
